import { useState } from "react";
import { newsPage } from "@/mocks/news";
import GlobalBottomCta from "@/components/feature/GlobalBottomCta";
import PageMeta from "@/components/feature/PageMeta";
import ScrollReveal from "@/components/feature/ScrollReveal";

const pageMeta = {
  title: "最新消息｜來加綉綉紅豆餅",
  description: "來加綉綉紅豆餅的最新動態、新品口味上市、市集活動、冷凍宅配優惠、加盟資訊與品牌合作公告。",
  keywords: "來加綉綉最新消息,紅豆餅活動,車輪餅新品,紅豆餅市集,紅豆餅優惠,紅豆餅品牌動態",
  ogType: "article",
};

const categoryColors: Record<string, string> = {
  "new-flavor": "bg-redbean-primary text-redbean-cream",
  delivery: "bg-redbean-caramel text-white",
  market: "bg-amber-600 text-white",
  franchise: "bg-redbean-gold text-redbean-coffee",
  brand: "bg-redbean-coffee text-redbean-cream",
};

export default function News() {
  const { hero, categories, articles } = newsPage;
  const [activeCat, setActiveCat] = useState("all");

  const filtered =
    activeCat === "all"
      ? articles
      : articles.filter((a) => a.category === activeCat);

  return (
    <main className="pt-16 md:pt-20">
      <PageMeta {...pageMeta} />
      {/* Hero */}
      <section className="relative min-h-[360px] md:min-h-[440px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-redbean-primary/40">
          <img
            src="https://readdy.ai/api/search-image?query=warm%20Taiwanese%20street%20food%20scene%20with%20taiyaki%20red%20bean%20cakes%20on%20display%20colorful%20signage%20cozy%20market%20atmosphere%20warm%20golden%20string%20lights%20artisan%20handmade%20snack%20stall%20food%20photography%20inviting%20warm%20tones%20shallow%20depth%20of%20field&width=1600&height=900&seq=news-hero-1&orientation=landscape"
            alt="來加綉綉紅豆餅最新消息與市集活動"
            className="w-full h-full object-cover object-center"
            loading="eager"
            decoding="async"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-redbean-coffee/55 via-redbean-coffee/45 to-redbean-coffee/60" />
        </div>
        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 text-center" style={{ maxWidth: "900px" }}>
          <span className="inline-block px-4 py-1.5 bg-redbean-gold text-redbean-coffee text-sm font-semibold rounded-full mb-6">
            {hero.tag}
          </span>
          <h1
            className="font-heading font-black text-redbean-cream mb-5 text-balance"
            style={{ fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.3 }}
          >
            {hero.title}
          </h1>
          <p className="text-redbean-cream/85 max-w-xl mx-auto" style={{ fontSize: "1.1875rem", lineHeight: 1.75 }}>
            {hero.subtitle}
          </p>
        </div>
      </section>

      {/* Filter + Articles */}
      <section className="py-12 md:py-16 bg-redbean-cream">
        <div className="mx-auto px-4 md:px-6 lg:px-8" style={{ maxWidth: "1200px" }}>
          {/* Category Tabs */}
          <ScrollReveal>
            <div className="flex flex-wrap justify-center gap-2 mb-10 md:mb-12">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCat(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    activeCat === cat.id
                      ? "bg-redbean-primary text-redbean-cream"
                      : "bg-white text-redbean-coffee hover:bg-redbean-lightcream shadow-soft"
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((article, idx) => (
              <ScrollReveal key={article.id} delay={idx * 100}>
                <div className="bg-white rounded-card p-6 shadow-soft hover:shadow-card transition-shadow duration-300 group">
                  <span
                    className={`inline-block px-3 py-1 text-xs font-semibold rounded-full mb-4 ${
                      categoryColors[article.category] || "bg-redbean-gold text-redbean-coffee"
                    }`}
                  >
                    {article.categoryLabel}
                  </span>
                  <h3
                    className="font-heading font-bold text-redbean-coffee mb-3 group-hover:text-redbean-primary transition-colors"
                    style={{ fontSize: "1.25rem", lineHeight: 1.5 }}
                  >
                    {article.title}
                  </h3>
                  <p className="text-redbean-coffee/60 text-[1rem] leading-relaxed mb-5">
                    {article.summary}
                  </p>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-redbean-caramel text-sm font-medium hover:text-redbean-primary transition-colors"
                  >
                    閱讀更多
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line" />
                    </span>
                  </button>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
      <GlobalBottomCta />
    </main>
  );
}